import { RefObject, useLayoutEffect, useRef } from 'react';

/**
 * Maintains scroll position when content is prepended to a reversed scroll container.
 *
 * Standard pattern (scrollHeight delta) used by Discord, Telegram Web, Rocket.Chat.
 * Captures scrollHeight when loading starts, then adjusts scrollTop by the height
 * difference when loading ends — keeping the user at the same visual position.
 *
 * Uses useLayoutEffect (after DOM mutation, before paint) so there is no flicker.
 */
export function useScrollAnchor(
  containerRef: RefObject<HTMLDivElement>,
  isLoading: boolean,
  enabled: boolean = true,
) {
  const prevScrollHeightRef = useRef(0);
  const wasLoadingRef = useRef(false);

  useLayoutEffect(() => {
    if (!enabled) return;

    const el = containerRef.current;
    if (!el) {
      wasLoadingRef.current = isLoading;
      return;
    }

    // Loading just finished — new content is in the DOM, loader is removed.
    // Shift scrollTop by the height difference so the viewport stays in place.
    if (wasLoadingRef.current && !isLoading) {
      const delta = el.scrollHeight - prevScrollHeightRef.current;
      if (delta > 0) {
        el.scrollTop += delta;
      }
    }

    // While loading, snapshot the current scrollHeight (includes loader).
    // This value is compared on the next render when loading finishes.
    if (isLoading) {
      prevScrollHeightRef.current = el.scrollHeight;
    }

    wasLoadingRef.current = isLoading;
  }, [isLoading, enabled]);
}
